import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DatePicker } from '@/components/common/DatePicker';
import SubmitButton from '@/components/common/SubmitButton';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import { Switch } from '@/components/shadcn/switch';
import { Textarea } from '@/components/shadcn/textarea';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import type { Party } from '@/features/party/types';

import useActionsAssu from '../../hooks/useActionsAssu';
import useEnsureEmailDocument from '../../hooks/useEnsureEmailDocument';
import useAssuEmployees from '../../queries/hooks/useAssuEmployees';
import AssigneeField from '../AssigneeField';
import PriorityField from '../PriorityField';

import type { ExportEmailValuesAssu } from './types';

type Props = ModalProps & {
  defaultValues?: Partial<ExportEmailValuesAssu>;
  party: Maybe<Party>;
};

const CRM_KEY = 'assu';

export default function ExportEmailModalAssu({
  onClose,
  defaultValues,
  party,
}: Props) {
  const { t } = useTranslation();

  const ensureEmailDocument = useEnsureEmailDocument();

  const { data: employees } = useAssuEmployees();

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    watch,
  } = useForm<ExportEmailValuesAssu>({
    defaultValues: {
      createTask: true,
      title: defaultValues?.title,
      description: defaultValues?.description,
      date: defaultValues?.date ? new Date(defaultValues.date) : new Date(),
      priority: defaultValues?.priority ?? 1,
    },
    shouldUnregister: true,
  });

  const assuActions = useActionsAssu();
  const { getSelectedMessageId } = useEmailClient();

  const crmPartyId = party?.external_refs.find(
    (ref) => ref.company.toLowerCase() === CRM_KEY,
  )?.ref;

  const onSuccess = () => {
    posthog.capture('Email exported to CRM', {
      crmBackend: CRM_KEY,
      partyId: party?.id,
      messageId: getSelectedMessageId(),
      withTask: Boolean(createTask),
    });
    toast.success(t('EMAIL_EXPORT_SUCCESS'));
    onClose();
  };

  const onError = (error: Error) => {
    toast.error(t('EMAIL_EXPORT_ERROR'));
    Sentry.captureException(error);
  };

  const onSubmit: SubmitHandler<ExportEmailValuesAssu> = async (data) => {
    try {
      if (!crmPartyId) {
        throw new Error('Missing crmPartyId when exporting to assu');
      }

      const { filename, link } = await ensureEmailDocument();

      if (createTask) {
        await assuActions.createTask(
          {
            title: data.title,
            description: data.description,
            priority: data.priority,
            dueDate: format(data.date, 'yyyy-MM-dd'),
            assignedTo: data.assignee?.value,
            documents: [
              {
                filename,
                link,
                description: data.description,
              },
            ],
            crmPartyId,
          },
          {
            onSuccess,
            onError,
          },
        );
      } else {
        await assuActions.uploadDocuments(
          {
            crmPartyId,
            documents: [
              {
                filename,
                link,
              },
            ],
          },
          {
            onSuccess,
            onError,
          },
        );
      }

      onClose();
    } catch (error) {
      toast.error(t('EMAIL_EXPORT_ERROR'));
      Sentry.captureException(error);
    }
  };

  const createTask = watch('createTask');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <div className="mb-3">
          <Controller
            control={control}
            name="createTask"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Switch
                  name="createTask"
                  id="createTask"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />

                <FieldLabel htmlFor="createTask" className="cursor-pointer">
                  {t('EXPORT_EMAIL_CREATE_TASK_LABEL')}
                </FieldLabel>
              </Field>
            )}
          />
        </div>

        <Field>
          <FieldLabel htmlFor="title">{t('TASK_FIELD_TITLE')}</FieldLabel>
          <Input
            {...register('title', { required: true })}
            placeholder={t('TASK_FIELD_TITLE_PH')}
          />
        </Field>

        {createTask && (
          <>
            <Field>
              <FieldLabel htmlFor="description">
                {t('TASK_FIELD_DESCRIPTION')}
              </FieldLabel>
              <Textarea
                {...register('description', { required: createTask })}
                placeholder={t('TASK_FIELD_DESCRIPTION_PH')}
                className="h-48"
              />
            </Field>

            <PriorityField<ExportEmailValuesAssu>
              control={control}
              priorities={[
                { label: t('PRIORITY_ASSU_1'), value: 1 },
                { label: t('PRIORITY_ASSU_2'), value: 2 },
                { label: t('PRIORITY_ASSU_3'), value: 3 },
              ]}
            />

            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('TASK_FIELD_DUE_DATE')}</FieldLabel>
                  <DatePicker {...field} />
                </Field>
              )}
            />

            <AssigneeField control={control} employees={employees} />
          </>
        )}
      </div>

      <div className="flex justify-end">
        <SubmitButton isSubmitting={isSubmitting} disabled={!isValid}>
          {createTask
            ? t('EXPORT_EMAIL_AND_CREATE_TASK_SUBMIT_BUTTON')
            : t('EXPORT_EMAIL_SUBMIT_BUTTON')}
        </SubmitButton>
      </div>
    </form>
  );
}
